<script setup lang="ts">
// Dense «вакансия дня» row: a crawlable link to the public vacancy page that
// opens the real preview with video on a plain click.
import { AvatarWithFallback } from '~/components/ui/avatar'
import { Button } from '~/components/ui/button'
import ListingPreviewLink from '~/components/listing/ListingPreviewLink.vue'
import { formatSalary } from '~/composables/new-ui'
import type { SearchResult } from '~/entities/search/search-state'

const props = defineProps<{ item: SearchResult }>()

const title = computed(() => props.item.title || props.item.profession?.title || 'Вакансия')
const salary = computed(() => {
  const { min, max } = props.item.salary ?? {}
  if (!min && !max) return null
  return min && min === max ? `${min.toLocaleString('ru-RU')} ₽` : formatSalary(props.item.salary)
})
const badges = computed(() => [props.item.experience?.title, ...(props.item.schedules ?? []).map(schedule => schedule.name)]
  .filter((value): value is string => Boolean(value))
  .slice(0, 3))
</script>

<template>
  <article class="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card p-4 transition-shadow hover:shadow-md sm:flex-row sm:items-start sm:gap-4">
    <div class="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
      <AvatarWithFallback :src="item.company?.logo" :name="item.company?.name || title" shape="rounded" class="size-12 shrink-0 sm:size-14" />
      <div class="min-w-0 flex-1">
        <h3 class="text-base font-semibold leading-tight sm:text-lg">
          <ListingPreviewLink kind="vacancy" :id="item.id" class="hover:text-primary">{{ title }}</ListingPreviewLink>
        </h3>
        <p v-if="salary" class="mt-1 font-medium">{{ salary }}</p>
        <p class="mt-0.5 truncate text-sm text-muted-foreground">{{ item.company?.name }}</p>
        <div v-if="badges.length" class="mt-2 flex flex-wrap gap-1.5">
          <span v-for="badge in badges" :key="badge" class="rounded-full bg-muted px-2.5 py-0.5 text-xs">{{ badge }}</span>
        </div>
      </div>
    </div>
    <div class="flex gap-2 sm:shrink-0 sm:flex-col">
      <Button as-child size="sm" shape="pill" class="h-10 flex-1 px-4 sm:h-8 sm:flex-none">
        <ListingPreviewLink kind="vacancy" :id="item.id">
          <Icon name="tabler:player-play-filled" class="size-3.5" />
          <span class="sm:hidden">Смотреть видео</span>
          <span class="hidden sm:inline">Видео</span>
        </ListingPreviewLink>
      </Button>
      <Button as-child size="sm" shape="pill" variant="secondary" class="hidden h-8 px-4 sm:inline-flex">
        <NuxtLink to="/signup/applicant">Откликнуться</NuxtLink>
      </Button>
    </div>
  </article>
</template>
